import type { IconName, StackedIcon } from '../fontawesome';
import type { FunctionalityType } from './generated';
import { Router } from './Router';

export type MenuStatusType = 'ONLINE' | 'OFFLINE' | 'UNAVAILABLE' | 'HIDDEN';

interface IMenuItem {
    route: string;
    icon: IconName | StackedIcon;
    subItems: MenuItem[];
    visibleIn: 'ALL' | 'SMALL' | 'BIG';
    translationKey: string;
    name: string;
    eventNeeded: boolean;
    prependEventUID: boolean;
    groupStart?: boolean;
    badgecount?: number;
    badgetext?: string;
    status?: () => MenuStatusType;
    visibleCallback?: () => boolean;
    roles?: string[];
    shortcutKey?: string;
    functionalityTypes?: FunctionalityType[];
}

export class MenuItem {
    public route: string;
    public icon: IconName | StackedIcon;
    public subItems: MenuItem[];
    public visibleIn: 'ALL' | 'SMALL' | 'BIG';
    public translationKey: string;
    public name: string;
    public eventNeeded: boolean;
    public prependEventUID: boolean;
    public groupStart?: boolean;
    public badgecount?: number | undefined;
    public badgetext?: string | undefined;
    public roles?: string[];
    public shortcutKey?: string;
    public functionalityTypes?: FunctionalityType[];

    private status?: () => MenuStatusType;

    private visibleCallback?: () => boolean;

    public constructor(menuitemdesc: IMenuItem) {
        this.prependEventUID = menuitemdesc.prependEventUID;
        this.route = menuitemdesc.route;
        this.icon = menuitemdesc.icon;
        this.subItems = menuitemdesc.subItems;
        this.visibleIn = menuitemdesc.visibleIn;
        this.translationKey = menuitemdesc.translationKey;
        this.name = menuitemdesc.name;
        this.eventNeeded = menuitemdesc.eventNeeded;
        this.groupStart = menuitemdesc.groupStart;
        this.badgecount = menuitemdesc.badgecount;
        this.badgetext = menuitemdesc.badgetext;
        this.status = menuitemdesc.status;
        this.visibleCallback = menuitemdesc.visibleCallback;
        this.shortcutKey = menuitemdesc.shortcutKey;
        this.roles = menuitemdesc.roles;
        this.functionalityTypes = menuitemdesc.functionalityTypes;
    }

    public getStatus = (): MenuStatusType => {
        if (this.status instanceof Function) {
            return this.status();
        }
        return 'HIDDEN';
    };

    public getVisible = (): boolean => {
        if (this.visibleCallback instanceof Function) {
            return this.visibleCallback();
        }
        return true;
    };

    public key = () => {
        return this.translationKey.replace('.', '_');
    };

    public isVisible = (size: 'SMALL' | 'BIG', eventID: number) => {
        return (eventID > 0 || !this.eventNeeded) && ['ALL', size].includes(this.visibleIn) && this.getVisible();
    };

    public path = (eventID: number) => {
        return (this.prependEventUID ? '/' + eventID : '') + this.route;
    };

    /**
     * Check if the current selected route is subroute of this
     * @param selectedroute
     * @returns
     */
    public hasActiveSubitem = (selectedroute: string) => {
        if (this.subItems.length == 0) return false;

        this.subItems.forEach((element) => {
            if (element.isInRoute(selectedroute, false, 0)) return true;
        });
        return false;
    };

    /**
     * Is the selected route this
     * @param selectedroute
     * @param leaf
     * @param eventID
     * @returns
     */
    public isInRoute = (selectedroute: string, leaf: boolean, eventID: number) => {
        const routeToCheck = Router.CurrentRouteRoot + '/'; // we add the trailing / to avoid partial matches ex /1234/races matching /1234/racesetup
        return this.path(eventID).includes(routeToCheck);
    };
}
